import { FC, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { PopUpHandler } from '../../shared/PopupHandler';
import { MessageTabs, XtMessage, XtremePushGlobal } from './types';
import DOMPurify from 'dompurify';
import { filterSpecialHTMLCharacters, joinStrings } from '../../../utils/string';
import { Button } from '../../shared/Button';
import { Spinner } from '../../shared/Spinner';
import { ReactComponent as MessageStatusIcon } from './../../../assets/Itv/bell-message.svg';
import { FaChevronLeft } from '@react-icons/all-files/fa/FaChevronLeft';
import { getMessageCreateTime } from './helpers';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import './ItvMessages.scss';
import { NativeEventBus } from '../../../native/NativeEventBus';
import { NATIVE_INBOX_TIMEOUT } from '../../../consts';

const MESSAGES_LOAD_STEP = 10;
const MESSAGES_COUNT_EDGE = 10;
const isNativeApp = NativeEventBus.isNativeBridgeAvailable();

export const ItvMessages: FC<{
  isOpen: boolean;
  msgCount?: number;
  setIsOpen: (arg: boolean) => void;
  setMsgCount: (arg: number) => void;
}> = ({ isOpen, setIsOpen, setMsgCount, msgCount }) => {
  const sanitize = DOMPurify.sanitize;
  const prevTab = useRef(MessageTabs.ALL);
  const { t } = useTranslations();

  const [messages, setMessages] = useState<XtMessage[]>([]);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [loadMoreLimit, setLoadMoreLimit] = useState<number>(MESSAGES_LOAD_STEP);
  const [isNewMsgFetching, setIsNewMsgFetching] = useState(false);
  const [isInitFetching, setIsInitFetching] = useState(true);
  const [allMsgCount, setAllMsgCount] = useState<number | undefined>();
  const [activeTab, setActiveTab] = useState<MessageTabs>(MessageTabs.ALL);
  const [msgOpened, setMsgOpened] = useState<{ id: number; shouldOpen: boolean } | null>();
  const [refetchMsgs, setRefetchMsgs] = useState(false);
  const [refetchMsgCount, setRefetchMsgCount] = useState(false);
  const [nativeOffset, setNativeOffset] = useState(0);
  const nativeEventBus = useMemo(() => new NativeEventBus(), []);

  const reduceBadge = (resBadge: undefined | number): false | void => resBadge !== undefined && setMsgCount(resBadge);

  const handleCount = (count?: number): string | number =>
    Number(count) >= MESSAGES_COUNT_EDGE ? `${MESSAGES_COUNT_EDGE - 1}+` : String(count);

  const markAsOpened = (id: number): void => {
    nativeEventBus.markMessageOpened({ id });
  };

  const resetMessagesState = (): void => {
    setMessages([]);
    setLoadMoreLimit(MESSAGES_LOAD_STEP);
    setNativeOffset(0);
    setIsInitFetching(true);
  };

  const handleTabOpen = (tab: MessageTabs): void => {
    const isNotCurrentTab = activeTab !== tab;
    const isMsgOpenedAndTabActive = msgOpened && activeTab === tab;

    if (isNotCurrentTab || isMsgOpenedAndTabActive) {
      setActiveTab(tab);
      setMsgOpened(null);
      resetMessagesState();
    }

    // Open tab straight from message expanded view
    isMsgOpenedAndTabActive && setRefetchMsgs(!refetchMsgs);
  };

  const onBackClick = (): void => {
    if (isNativeApp) {
      setMsgOpened(null);

      if (msgOpened?.shouldOpen) {
        setMessages((prevMessages) => prevMessages.map((m) => (m.id === msgOpened.id ? { ...m, opened: 1 } : m)));
      }
    } else {
      setMsgOpened(null);

      if (msgOpened?.shouldOpen) {
        resetMessagesState();
        setRefetchMsgs(!refetchMsgs);
      }
    }
  };

  const notFoundMsgs = useMemo(
    () => [
      {
        condition: activeTab === MessageTabs.ALL && !allMsgCount,
        text: t('messages.absent.all'),
      },
      {
        condition: activeTab === MessageTabs.UNREAD && !msgCount,
        text: t('messages.absent.unread'),
      },
      {
        condition: activeTab === MessageTabs.READ && !messages.length,
        text: t('messages.absent.read'),
      },
    ],
    [messages]
  );

  useEffect(() => {
    if (isNativeApp) return;
    setIsNewMsgFetching(true);

    const isSameTab = prevTab.current === activeTab;
    const resultLimit = isSameTab ? loadMoreLimit : MESSAGES_LOAD_STEP;

    (window as unknown as XtremePushGlobal).xtremepush(
      'inbox',
      'message.list',
      {
        limit: MESSAGES_LOAD_STEP,
        offset: resultLimit - MESSAGES_LOAD_STEP,
        ...(activeTab !== MessageTabs.ALL && { opened: Number(activeTab === MessageTabs.READ) }),
      },
      (result) => {
        setMessages((prevMessages) =>
          prevTab.current === activeTab ? [...prevMessages, ...result.items] : result.items
        );
        setIsNewMsgFetching(false);
        setIsInitFetching(false);

        reduceBadge(result.badge);
        setShowLoadMore(result.more);
      },
      (error) => {
        setIsNewMsgFetching(false);
        console.log(error);
      }
    );
  }, [activeTab, loadMoreLimit, refetchMsgs]);

  useEffect(() => {
    if (isNativeApp) return;
    (window as unknown as XtremePushGlobal).xtremepush(
      'inbox',
      'message.count',
      {},
      (result) => setAllMsgCount(Number(result.count)),
      (error) => console.log(error)
    );
  }, [refetchMsgCount]);

  useEffect(() => {
    if (!msgOpened?.shouldOpen) return;

    if (isNativeApp) {
      nativeEventBus.markMessageOpened({ id: msgOpened.id });
    } else {
      (window as unknown as XtremePushGlobal).xtremepush(
        'inbox',
        'message.action',
        { id: msgOpened.id, open: 1 },
        (result) => {
          setMsgCount(result.badge);
          setRefetchMsgCount(!refetchMsgCount);
        },
        (error) => console.error(error)
      );
    }
  }, [msgOpened?.shouldOpen]);

  useEffect(() => {
    if (!isNewMsgFetching) {
      prevTab.current = activeTab;
    }
  }, [isNewMsgFetching]);

  useEffect(() => {
    if (!isNativeApp) return;

    setIsInitFetching(true);
    setIsNewMsgFetching(true);
    setMessages([]);
    setNativeOffset(0);

    const inboxHandler = (event: MessageEvent): void => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        const msgsRaw = data.payload?.messages || data.items || [];
        const msgs = typeof msgsRaw === 'string' ? JSON.parse(msgsRaw) : msgsRaw;
        const offset = data.payload?.offset ?? 0;

        const normalized = msgs.map((m: XtMessage) => ({
          ...m,
          message:
            typeof m.message === 'object'
              ? { title: m.message.title || '', alert: m.message.alert || '' }
              : { title: m.message, alert: '' },
          create_time: m.create_time || Date.now(),
        }));

        setMessages((prev) => {
          if (offset === 0) return normalized;

          const map = new Map(prev.map((m) => [m.id, m]));

          for (const msg of normalized) {
            map.set(msg.id, msg);
          }

          return Array.from(map.values());
        });
        setAllMsgCount(data.payload?.count ?? normalized.length);
        setShowLoadMore(data.payload?.more ?? data.more ?? false);
      } catch (err) {
        console.error('Failed parsing native inbox event', err);
      } finally {
        setIsInitFetching(false);
        setIsNewMsgFetching(false);
      }
    };

    const badgeHandler = (event: MessageEvent): void => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        const badge = data.payload?.badge ?? data.badge;

        if (badge !== undefined) setMsgCount(badge);
      } catch {
        console.error('Failed parsing native badge event');
      }
    };

    nativeEventBus.listenToEvent('getInboxMessages', inboxHandler);
    nativeEventBus.listenToEvent('getInboxBadge', badgeHandler);

    setTimeout(() => {
      nativeEventBus.getInboxMessages({
        limit: MESSAGES_LOAD_STEP,
        offset: 0,
        filterOpened: activeTab === MessageTabs.UNREAD ? 0 : activeTab === MessageTabs.READ ? 1 : undefined,
      });
    }, NATIVE_INBOX_TIMEOUT);

    return () => {
      nativeEventBus.removeListener('getInboxMessages', inboxHandler);
      nativeEventBus.removeListener('getInboxBadge', badgeHandler);
    };
  }, [nativeEventBus, activeTab, refetchMsgs]);

  const getMessageDot = (message: XtMessage, className?: string): boolean | ReactElement =>
    !(message.opened || msgOpened?.shouldOpen) && (
      <div className={joinStrings(['itv-messages__message-dot', className && `itv-messages__${className}`])} />
    );

  const isTitleContentPresent = (message: XtMessage): boolean => {
    const parser = new DOMParser();

    let isContentPresent = false;
    const titleDOM = parser.parseFromString(message.message?.title || '', 'text/html');

    const titleNodes = titleDOM.querySelectorAll('*');

    titleNodes.forEach((node) => {
      if (node.textContent) isContentPresent = true;
    });

    return isContentPresent;
  };

  return (
    <PopUpHandler
      backdropClassName="itv-messages"
      isOpen={isOpen}
      header={
        (
          <div className="itv-messages__header">
            <div className="itv-messages__inner-header">
              {msgOpened && <FaChevronLeft className="itv-messages__chevron" onClick={onBackClick} />}
              {t('messages.title')}
              <div className="itv-messages__tabs">
                {Object.values(MessageTabs).map((mesageTab) => {
                  const isAllMsgTab = mesageTab === MessageTabs.ALL;
                  const isUnreadMsgTab = mesageTab === MessageTabs.UNREAD;
                  const isReadMsgTab = mesageTab === MessageTabs.READ;

                  return (
                    <div
                      key={mesageTab}
                      className={joinStrings([
                        'itv-messages__tab',
                        activeTab === mesageTab && 'itv-messages__tab--active',
                      ])}
                      onClick={(): void => handleTabOpen(mesageTab)}
                    >
                      {isAllMsgTab && t('messages.all')}
                      {isUnreadMsgTab && t('messages.unread')}
                      {isReadMsgTab && t('messages.read')}
                      <>
                        {isAllMsgTab && Boolean(allMsgCount) && (
                          <div className="itv-messages__message-counter">{handleCount(allMsgCount)}</div>
                        )}
                        {isUnreadMsgTab && Boolean(msgCount) && (
                          <div className="itv-messages__message-counter">{handleCount(msgCount)}</div>
                        )}
                      </>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) as unknown as string
      }
      onClose={(): void => setIsOpen(false)}
    >
      <div className="itv-messages__messages-section">
        {isInitFetching ? (
          <Spinner />
        ) : (
          <>
            {messages.map((message) => {
              const isTitle = isTitleContentPresent(message);
              const isExpanded = msgOpened?.id === message.id;

              return (
                (msgOpened ? msgOpened.id === message.id : true) && (
                  <div
                    key={message.id}
                    className={joinStrings([
                      'itv-messages__message-wrapper',
                      !message.opened && 'itv-messages__message-wrapper--unread',
                      isExpanded && 'itv-messages__message-wrapper--expanded',
                    ])}
                    onClick={(): void => {
                      setMsgOpened({ id: message.id, shouldOpen: !message.opened });

                      if (isNativeApp) {
                        markAsOpened(message.id);
                        setMessages((prevMessages) =>
                          prevMessages.map((m) => (m.id === message.id ? { ...m, opened: 1 } : m))
                        );
                      }
                    }}
                  >
                    <div
                      className={joinStrings([
                        'itv-messages__message-status-badge',
                        !message.opened && 'itv-messages__message-status-badge--unread',
                      ])}
                    >
                      <MessageStatusIcon className="itv-messages__message-status-icon" />
                    </div>
                    <div
                      className={joinStrings([
                        'itv-messages__message-content',
                        msgOpened && 'itv-messages__message-content--expanded',
                      ])}
                    >
                      <div className="itv-messages__message-title">
                        {isTitle && getMessageDot(message)}
                        <div
                          dangerouslySetInnerHTML={{
                            __html: filterSpecialHTMLCharacters(sanitize(message.message?.title || '')),
                          }}
                        />
                      </div>
                      <div
                        className={joinStrings([
                          'itv-messages__message-content',
                          msgOpened && 'itv-messages__message-content--expanded',
                        ])}
                      >
                        {!isTitle && getMessageDot(message, 'message-fallback')}
                        <div
                          dangerouslySetInnerHTML={{
                            __html: filterSpecialHTMLCharacters(sanitize(message.message?.alert || '')),
                          }}
                        />
                      </div>

                      <div className="itv-messages__message-time">
                        {getMessageCreateTime(message.create_time, t('messages.at'))}
                      </div>
                    </div>
                  </div>
                )
              );
            })}
            {showLoadMore && !msgOpened && (
              <Button
                className="itv-messages__more-btn"
                disabled={isNewMsgFetching}
                isSubmitting={isNewMsgFetching}
                variant="secondary"
                type="button"
                title="Load More"
                onClick={(): void => {
                  if (isNativeApp && !isNewMsgFetching) {
                    setIsNewMsgFetching(true);
                    const newOffset = nativeOffset + MESSAGES_LOAD_STEP;

                    setNativeOffset(newOffset);

                    nativeEventBus.getInboxMessages({
                      limit: MESSAGES_LOAD_STEP,
                      offset: newOffset,
                      filterOpened:
                        activeTab === MessageTabs.UNREAD ? 0 : activeTab === MessageTabs.READ ? 1 : undefined,
                    });
                  } else {
                    setLoadMoreLimit((prev) => prev + MESSAGES_LOAD_STEP);
                  }
                }}
              >
                {t('messages.view-more')}
              </Button>
            )}
            {notFoundMsgs.map(
              (notFoundMsg) =>
                notFoundMsg.condition && (
                  <div key={notFoundMsg.text} className="itv-messages__not-found">
                    {notFoundMsg.text}
                  </div>
                )
            )}
          </>
        )}
      </div>
    </PopUpHandler>
  );
};
